import { useBrowerSuggestionTip } from "@uiview/views/BrowerSuggestionTip/useBrowerSuggestionTip";
import { AuthStore } from "imagica-corekit/dist/base/store/AuthStore";
import { FotAuthStore } from "imagica-corekit/dist/base/store/FotAuthStore";
import { PreferencesStore } from "imagica-corekit/dist/base/store/PreferencesStore";
import { SubscriptionStore } from "imagica-corekit/dist/cases/store/SubscriptionStore";
import { HomePluginStore } from "imagica-corekit/dist/base/store/HomePluginStore";
import { settings } from "imagica-corekit/dist/base/kernel/Settings";

import useSaveSubscriber from "./useSaveSubscriber";
import useEditorPermission from "./useEditorPermission";
import useUserAuth from "./useUserAuth";

import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useSignal } from "@preact/signals-react";
import { useDispatch, useSelector } from "react-redux";

import func from "@uikit/func";
import { getIt } from "@uikit/getIt";
import store from "@store/index";
import { fotActions } from "@store/fot";
import { editorActions } from "@store/editor";
import { isAuth } from "@uikit/login";
import { boolean } from "yup";
import { useCallback } from "react";
import { logEvent } from "@uikit/service/amplitude";
import isBlank from "@sedan-utils/is-blank";

const authStore: AuthStore = getIt(AuthStore);
const preferencesStore: PreferencesStore = getIt(PreferencesStore);
const subscriptionStore: SubscriptionStore = getIt(SubscriptionStore);
const homePluginStore: HomePluginStore = getIt(HomePluginStore);

export function useInitPage() {
  const { getSubscriberParamsData } = useSaveSubscriber();
  const { setBrowerSuggestionTip } = useBrowerSuggestionTip();
  const { enableBrainStudios } = useEditorPermission();
  const [searchParams] = useSearchParams();
  const { getUserMe, userMeState } = useUserAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const userMe = useSelector((state: any) => state.editor.me);

  const setShowShareSignin = (val: boolean) => {
    dispatch(fotActions.setShowShareSignin(val));
  };
  const setIsLogin = (val: boolean) => {
    dispatch(fotActions.setIsLogin(val));
  };
  const setMe = (val: any) => {
    dispatch(editorActions.setMe(val));
  };
  const setLoginMethod = (val: any) => {
    dispatch(editorActions.setLoginMethod(val));
  };

  const isInitLoading = useSignal(true);

  const init = async () => {
    try {
      isInitLoading.value = true;
      //获取订阅信息
      await getSubScriber();
      //处理是否为share
      const data = store.getState().editor.subscriberParamsData as any;
      // data为空，表示unpublish
      const isShare =
        location.pathname.includes("/editor/shared_intelligent_app") ||
        isBlank(data) ||
        data?.attributes?.mode === "subscriber";
      homePluginStore.setCustomDomain(data?.imagica_byod || "");
      homePluginStore.setIsShare(isShare);
      //是否显示改用PC chrome的提示
      setBrowerSuggestionTip();
      //获取用户信息
      if ((!isBlank(authStore.state.brainToken) || !isShare) && isBlank(userMe)) {
        await getUserMe();
      } else {
        await getUserMe("", true);
      }
      //查看是否有页面权限 || 是否需要跳转editor
      clickEnableBrainStudios();
    } catch (error) {
      func.messageUtil("Init page failed");
    } finally {
      isInitLoading.value = false;
    }

    const isAuths: boolean = isAuth() as boolean;
    setIsLogin(isAuths);
  };

  const getSubScriber = async () => {
    try {
      const data = await getSubscriberParamsData();

      const subGuestToken = data?.guest_user_key;
      if (subGuestToken) {
        sessionStorage.setItem("GUEST_TOKEN", subGuestToken);
        authStore.loginGuest(subGuestToken);
      } else {
        const guestToken = searchParams.get("guest_user_key");
        if (guestToken) {
          sessionStorage.setItem("GUEST_TOKEN", guestToken);
          authStore.loginGuest(guestToken);
        }
      }
    } catch (error) {
      console.error("get studios failed.", error);
      // STUD-1646：获取分享链接数据失败，无需登录直接显示 unpublish tips 页面
      setShowShareSignin(true);
    }
  };

  const clickEnableBrainStudios = () => {
    const subscriber = store.getState().editor.subscriberParamsData as any;
    // subscriber为空，表示unpublish, 直接显示提示
    if (isBlank(subscriber)) {
      setShowShareSignin(true);
      return;
    }
    //没有页面权限，去到登录页面
    if (!homePluginStore.state.isShare && !enableBrainStudios) {
      // 清除token
      clickLogOut();
      // func.customMsg({
      //   content: (
      //     <p>
      //       Please request early access at <a href="https://www.imagica.ai/">Imagica.ai</a>
      //     </p>
      //   ),
      // });
    }

    linkToCreator();
    // 进入/editor/shared_intelligent_app，不是订阅，跳转到editor.经测试create.imagica.ai会自动补全路径，加上/editor，因此不需要配置完整路径
    // else if (location.pathname.includes("denied-tip") || location.pathname.includes("/editor/shared_intelligent_app")) {
    //   navigate("/editor");
    // }
  };
  const linkToCreator = () => {
    const localDomain = window.location.hostname;
    const isShareDomain = !isBlank(homePluginStore.state.customDomain) || settings.shareDomain.includes(localDomain);
    // 我们自己创作者方的域名不跳转，解决一直跳转陷入无限循环
    // 不为第三方的域名，也不是我们自己专门的share域名，都是创作者域名
    if (!isShareDomain) return;
    // 当前是subscriber, 去掉fot/#/editor
    if (homePluginStore.state.isShare) {
      const url = window.location.href.replace(/\/fot(\/)*#\/editor(\/)*/, "");
      window.history.pushState({}, "", url);
      return;
    }
    // 当前是creator, 跳转到我们自己的editor链接
    let imagicaUrl = settings.ImagicaUrl.replace(/\/editor\/$/, "");
    let path = location.pathname;
    if (path === "/") {
      path = "/editor";
    }
    let hash = `${path}${location.search}`;
    const url = `${imagicaUrl}${hash}`;
    window.location.href = url;
  };

  const clickLogOut = useCallback(() => {
    logEvent("logout");
    const authStore: FotAuthStore = getIt(FotAuthStore);

    authStore.removeBrainToken();
    preferencesStore.dirty();
    subscriptionStore.dirty();
    localStorage.removeItem("BRAIN_USER_EMAIL");
    navigate("/signin");
    setMe({});
    setIsLogin(false);
    setLoginMethod("");

    authStore.logout();
  }, []);

  return {
    init,
    isInitLoading,
    clickLogOut,
  };
}
